import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TimeoutError, firstValueFrom, timeout } from 'rxjs';

import { ApiService } from '@core/api/api.service';
import {
  CategoryCode,
  ClassificationAutosavedItem,
  ClassificationGraphStep,
  ClassificationModelInfo,
  ClassificationModelVote,
  ClassificationObservation,
  ClassificationObservationDetail,
  ClassificationPreview,
  ClassificationProposal,
  ClassificationProposalProduct,
  TypeCode
} from '@core/models/models';

@Component({
  selector: 'app-classification-admin',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './classification-admin.component.html',
  styleUrl: './classification-admin.component.css'
})
export class ClassificationAdminComponent implements OnInit, OnDestroy {
  private readonly api = inject(ApiService);

  barcode = '';
  verifiedBy = '';
  loading = false;
  verifying = false;
  clearing = false;
  errorMessage = '';
  statusMessage = '';
  preview: ClassificationPreview | null = null;
  view: 'needs' | 'autosaved' = 'needs';
  proposals: ClassificationProposal[] = [];
  proposalProducts: ClassificationProposalProduct[] = [];
  /** True while the open proposal's products are loading. */
  proposalProductsLoading = false;
  selectedProposalId: number | null = null;
  autosaved: ClassificationAutosavedItem[] = [];
  autosavedModel = '';
  runningGraph = false;
  classifyModels: ClassificationModelInfo[] = [];
  private previewRequestId = 0;

  departments: CategoryCode[] = [];
  categories: CategoryCode[] = [];
  types: TypeCode[] = [];
  private categoryBySlug = new Map<string, CategoryCode>();
  private typeBySlug = new Map<string, TypeCode>();
  selectedDepartmentSlug = '';
  selectedCategorySlug = '';
  selectedTypeSlug = '';
  hidingNode = false;

  newTypeSlug = '';
  newTypeNameHe = '';
  creatingType = false;
  selectedObservationMethod: string | null = null;
  ioPopup: { title: string; subtitle: string | null; input: string; output: string } | null = null;
  imagePopupOpen = false;
  private imagePopupCloseTimer: ReturnType<typeof setTimeout> | null = null;

  async ngOnInit(): Promise<void> {
    await this.loadCodes();
    await this.loadClassifyModels();
    await this.loadProposals();
  }

  ngOnDestroy(): void {
    this.cancelCloseImagePopup();
  }

  get groupsForDepartment(): CategoryCode[] {
    if (!this.selectedDepartmentSlug) {
      return this.categories;
    }
    return this.categories.filter((c) => c.descriptionHe === this.selectedDepartmentSlug);
  }

  get typesForCategory(): TypeCode[] {
    const filtered = !this.selectedCategorySlug
      ? this.types
      : this.types.filter((t) => t.categorySlug === this.selectedCategorySlug);
    if (!this.selectedTypeSlug || filtered.some((t) => t.slug === this.selectedTypeSlug)) {
      return filtered;
    }
    const selected = this.types.find((t) => t.slug === this.selectedTypeSlug);
    return selected ? [selected, ...filtered] : filtered;
  }

  get usableClassifyModels(): ClassificationModelInfo[] {
    return this.classifyModels.filter((m) => m.available && m.voting);
  }

  get ensembleModelLabels(): string {
    return this.usableClassifyModels.map((m) => m.label).join(' · ');
  }

  async loadClassifyModels(): Promise<void> {
    try {
      const res = await firstValueFrom(this.api.listClassificationModels());
      this.classifyModels = res.models || [];
    } catch {
      this.classifyModels = [];
      this.errorMessage = 'טעינת רשימת מודלי הסיווג נכשלה. רעננו את הדף.';
    }
  }

  async loadCodes(): Promise<void> {
    try {
      const codes = await firstValueFrom(this.api.listClassificationCodes());
      this.departments = codes.departments ?? [];
      this.categories = codes.categories ?? [];
      this.types = codes.types ?? [];
      this.categoryBySlug = new Map(this.categories.map((c) => [c.slug, c]));
      this.typeBySlug = new Map(this.types.map((t) => [t.slug, t]));
    } catch {
      this.departments = [];
      this.categories = [];
      this.types = [];
      this.categoryBySlug = new Map();
      this.typeBySlug = new Map();
      this.errorMessage = 'טעינת עץ רמי לוי נכשלה.';
    }
  }

  /** Hebrew labels come from the code tables; reload if the first fetch failed. */
  private async ensureCodes(): Promise<boolean> {
    if (this.categoryBySlug.size > 0 && this.typeBySlug.size > 0) {
      return true;
    }
    await this.loadCodes();
    return this.categoryBySlug.size > 0 && this.typeBySlug.size > 0;
  }

  async previewLookup(): Promise<void> {
    const code = this.barcode.trim();
    if (code.length === 0) {
      this.errorMessage = 'נא להזין ברקוד.';
      this.preview = null;
      return;
    }

    const requestId = ++this.previewRequestId;
    this.loading = true;
    this.errorMessage = '';
    this.statusMessage = '';
    this.closeImagePopup();
    this.closeIoPopup();
    this.selectedObservationMethod = null;
    try {
      if (!(await this.ensureCodes())) {
        if (requestId !== this.previewRequestId) {
          return;
        }
        this.errorMessage = 'טעינת קבוצות ותת-קבוצות נכשלה.';
        this.loading = false;
        return;
      }
      // Phrase + centroid + neighbor. Zero-shot NLI is not used.
      const result = await firstValueFrom(
        this.api.previewClassification(code, { includeZeroShot: false }).pipe(timeout(45_000))
      );
      if (requestId !== this.previewRequestId) {
        return;
      }
      this.preview = result;
      this.selectedCategorySlug = result.verifiedCategorySlug ?? this.pickSuggestedCategory(result) ?? '';
      this.selectedTypeSlug = result.verifiedTypeSlug ?? this.pickSuggestedType(result) ?? '';
      if (!this.selectedCategorySlug && this.selectedTypeSlug) {
        const type = this.types.find((t) => t.slug === this.selectedTypeSlug);
        if (type) {
          this.selectedCategorySlug = type.categorySlug;
        }
      }
      const group = this.categories.find((c) => c.slug === this.selectedCategorySlug);
      if (group?.descriptionHe) {
        this.selectedDepartmentSlug = group.descriptionHe;
      }
      // Refresh model list after backend may have been restarting.
      void this.loadClassifyModels();
      this.loading = false;
    } catch (error) {
      if (requestId !== this.previewRequestId) {
        return;
      }
      this.preview = null;
      if (error instanceof HttpErrorResponse && error.status === 404) {
        this.errorMessage = 'לא נמצא מוצר לברקוד הזה.';
      } else if (error instanceof TimeoutError) {
        this.errorMessage = 'סיווג מהיר לא חזר. נסו שוב בעוד רגע.';
      } else {
        this.errorMessage = 'טעינת הצעות הסיווג נכשלה.';
      }
      this.loading = false;
    }
  }

  async loadRandomProduct(options?: { afterVerifyMessage?: string }): Promise<void> {
    this.loading = true;
    this.errorMessage = '';
    if (!options?.afterVerifyMessage) {
      this.statusMessage = '';
    }
    try {
      const result = await firstValueFrom(
        this.api.getRandomClassificationProduct().pipe(timeout(15_000))
      );
      this.barcode = result.barcode;
      this.loading = false;
      await this.previewLookup();
      if (options?.afterVerifyMessage && !this.errorMessage) {
        this.statusMessage = options.afterVerifyMessage;
      }
    } catch (error) {
      this.loading = false;
      if (error instanceof HttpErrorResponse && error.status === 404) {
        this.errorMessage = 'לא נמצאו מוצרים עם ברקוד.';
      } else if (error instanceof TimeoutError) {
        this.errorMessage = 'טעינת מוצר אקראי לא חזרה. נסו שוב בעוד רגע.';
      } else {
        this.errorMessage = 'טעינת מוצר אקראי נכשלה.';
      }
    }
  }

  onDepartmentChange(): void {
    const group = this.categories.find((c) => c.slug === this.selectedCategorySlug);
    if (group && group.descriptionHe !== this.selectedDepartmentSlug) {
      this.selectedCategorySlug = '';
      this.selectedTypeSlug = '';
    }
  }

  onCategoryChange(): void {
    if (!this.selectedTypeSlug) {
      return;
    }
    const type = this.types.find((t) => t.slug === this.selectedTypeSlug);
    if (type && type.categorySlug !== this.selectedCategorySlug) {
      this.selectedTypeSlug = '';
    }
  }

  onTypeChange(): void {
    const type = this.types.find((t) => t.slug === this.selectedTypeSlug);
    if (type) {
      this.selectedCategorySlug = type.categorySlug;
      const group = this.categories.find((c) => c.slug === type.categorySlug);
      if (group?.descriptionHe) {
        this.selectedDepartmentSlug = group.descriptionHe;
      }
    }
  }

  async hideSelectedNode(level: 'group' | 'sub_group'): Promise<void> {
    const slug = level === 'group' ? this.selectedCategorySlug : this.selectedTypeSlug;
    const id = Number(slug);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }
    this.hidingNode = true;
    this.errorMessage = '';
    try {
      await firstValueFrom(this.api.setTaxonomyNodeHidden(id, true));
      if (level === 'group') {
        this.selectedCategorySlug = '';
        this.selectedTypeSlug = '';
      } else {
        this.selectedTypeSlug = '';
      }
      await this.loadCodes();
      this.statusMessage = 'הצומת הוסתר.';
    } catch {
      this.errorMessage = 'הסתרת הצומת נכשלה.';
    } finally {
      this.hidingNode = false;
    }
  }

  openObservationIo(observation: ClassificationObservation): void {
    this.ioPopup = {
      title: this.methodLabel(observation.method),
      subtitle: this.methodSubtitle(observation.method),
      input: this.observationIoInput(observation),
      output: this.observationIoOutput(observation)
    };
  }

  openGraphStepIo(step: ClassificationGraphStep): void {
    this.ioPopup = {
      title: this.stepTitle(step.name),
      subtitle: step.model ? `${step.model} · ${step.decision}` : step.decision,
      input: this.graphStepLlmInput(step) || '—',
      output: this.graphStepLlmOutput(step) || '—'
    };
  }

  hasGraphStepIo(step: ClassificationGraphStep): boolean {
    return !!(this.graphStepLlmInput(step) || this.graphStepLlmOutput(step));
  }

  private graphStepLlmInput(step: ClassificationGraphStep): string {
    const raw = step as ClassificationGraphStep & { llm_input?: string | null };
    return (step.llmInput ?? raw.llm_input ?? '').trim();
  }

  private graphStepLlmOutput(step: ClassificationGraphStep): string {
    const raw = step as ClassificationGraphStep & { llm_output?: string | null };
    return (step.llmOutput ?? raw.llm_output ?? '').trim();
  }

  closeIoPopup(): void {
    this.ioPopup = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.ioPopup) {
      this.closeIoPopup();
    }
  }

  observationIoInput(observation: ClassificationObservation): string {
    const detail = this.parseObservationDetail(observation);
    const hypotheses = detail?.inference?.hypotheses;
    if (observation.method.startsWith('zero_shot_')) {
      const premise = detail?.input?.text
        || this.preview?.displayName
        || this.preview?.nameRaw
        || '—';
      if (hypotheses?.length) {
        const lines = [`premise: ${premise}`, '', 'hypotheses:'];
        for (const hypothesis of hypotheses) {
          lines.push(`• ${hypothesis.text}`);
        }
        return lines.join('\n');
      }
      return `premise: ${premise}`;
    }

    const labels = detail?.input?.labels;
    if (detail?.input?.text && labels?.length) {
      const lines = [`text: ${detail.input.text}`, '', 'phrases:'];
      for (const label of labels) {
        lines.push(`• ${label.phrase}`);
      }
      return lines.join('\n');
    }

    if (
      observation.method.startsWith('embedding_centroid_') ||
      observation.method === 'verified_neighbor'
    ) {
      return detail?.input?.text
        || this.preview?.displayName
        || this.preview?.nameRaw
        || '—';
    }

    if (detail?.input) {
      return JSON.stringify(detail.input, null, 2);
    }

    const text = this.preview?.displayName || this.preview?.nameRaw;
    if (!text) {
      return '—';
    }
    return text;
  }

  observationIoOutput(observation: ClassificationObservation): string {
    const detail = this.parseObservationDetail(observation);
    const labels = detail?.input?.labels;
    const scores = detail?.output?.scores ?? detail?.top ?? [];
    const hypotheses = detail?.inference?.hypotheses;
    if (detail?.output?.metric === 'entailment_probability' && hypotheses?.length && scores.length > 0) {
      const hypothesesBySlug = new Map(
        hypotheses.map((hypothesis) => [hypothesis.slug, hypothesis.text])
      );
      const lines = ['entailment probability:'];
      for (const score of scores) {
        lines.push(
          `• ${hypothesesBySlug.get(score.slug) ?? score.slug}: ${score.confidence.toFixed(4)}`
        );
      }
      return lines.join('\n');
    }

    if (labels?.length && scores.length > 0) {
      const phrasesBySlug = new Map(labels.map((label) => [label.slug, label.phrase]));
      const lines = ['cosine similarity:'];
      for (const score of scores) {
        const phrase = phrasesBySlug.get(score.slug);
        lines.push(`• ${phrase ?? score.slug}: ${score.confidence.toFixed(4)}`);
      }
      return lines.join('\n');
    }

    const lines = [
      `קבוצה: ${this.categoryName(observation.categorySlug)}`,
      `תת-קבוצה: ${this.typeName(observation.typeSlug)}`,
      `ביטחון: ${observation.confidence.toFixed(2)}`
    ];

    if (scores.length > 0) {
      lines.push('', 'דירוג:');
      for (const score of scores) {
        lines.push(`• ${this.slugLabel(score.slug)} — ${score.confidence.toFixed(2)}`);
      }
    }

    if (detail?.output?.neighborName || detail?.output?.neighborProductId) {
      const neighbor = detail.output.neighborName || '—';
      const id = detail.output.neighborProductId != null ? ` (#${detail.output.neighborProductId})` : '';
      lines.push('', `שכן מאומת: ${neighbor}${id}`);
    }

    return lines.join('\n');
  }

  applyObservation(observation: ClassificationObservation): void {
    this.selectedObservationMethod = observation.method;
    if (observation.typeSlug) {
      this.selectedTypeSlug = observation.typeSlug;
      const type = this.types.find((t) => t.slug === observation.typeSlug);
      this.selectedCategorySlug = type?.categorySlug ?? observation.categorySlug ?? this.selectedCategorySlug;
      return;
    }
    if (observation.categorySlug) {
      this.selectedCategorySlug = observation.categorySlug;
      if (this.selectedTypeSlug) {
        const type = this.types.find((t) => t.slug === this.selectedTypeSlug);
        if (type && type.categorySlug !== this.selectedCategorySlug) {
          this.selectedTypeSlug = '';
        }
      }
    }
  }

  async verify(): Promise<void> {
    if (!this.preview) {
      return;
    }
    if (!this.selectedCategorySlug || !this.selectedTypeSlug) {
      this.errorMessage = 'נא לבחור קבוצה ותת-קבוצה לפני אימות.';
      return;
    }

    this.verifying = true;
    this.errorMessage = '';
    this.statusMessage = '';
    try {
      const result = await firstValueFrom(
        this.api.verifyClassification({
          productId: this.preview.productId,
          categorySlug: this.selectedCategorySlug,
          typeSlug: this.selectedTypeSlug,
          verifiedBy: this.verifiedBy.trim() || null,
          notes: null
        })
      );
      const verifiedMsg = result.anchorSaved
        ? 'אומת. העוגן נשמר. מוצר הבא:'
        : 'אומת (ללא עוגן). מוצר הבא:';
      this.verifying = false;
      this.statusMessage = 'אומת. טוען מוצר הבא…';
      await this.loadRandomProduct({ afterVerifyMessage: verifiedMsg });
    } catch {
      this.errorMessage = 'אימות הסיווג נכשל.';
      this.verifying = false;
    }
  }

  async clearClassification(): Promise<void> {
    if (!this.preview) {
      return;
    }

    this.clearing = true;
    this.errorMessage = '';
    this.statusMessage = '';
    try {
      await firstValueFrom(this.api.clearClassification(this.preview.productId));
      this.applyVerifiedToPreview(null, null, null, null);
      this.statusMessage = 'הסיווג נוקה.';
    } catch {
      this.errorMessage = 'ניקוי הסיווג נכשל.';
    } finally {
      this.clearing = false;
    }
  }

  async createType(): Promise<void> {
    const nameHe = this.newTypeNameHe.trim();
    if (!nameHe || !this.selectedCategorySlug) {
      this.errorMessage = 'ליצירת תת-קבוצה נדרשים שם בעברית וקבוצה נבחרת.';
      return;
    }
    const slug = this.newTypeSlug.trim() || 'commpare';

    this.creatingType = true;
    this.errorMessage = '';
    try {
      const created = await firstValueFrom(
        this.api.createClassificationType({
          slug,
          categorySlug: this.selectedCategorySlug,
          nameHe,
          nameEn: null,
          descriptionHe: nameHe,
          sortOrder: 100
        })
      );
      await this.loadCodes();
      this.selectedTypeSlug = created.slug;
      this.newTypeSlug = '';
      this.newTypeNameHe = '';
      this.statusMessage = `נוספה תת-קבוצה: ${created.nameHe}`;
    } catch {
      this.errorMessage = 'יצירת תת-קבוצה חדשה נכשלה.';
    } finally {
      this.creatingType = false;
    }
  }

  categoryName(slug: string | null | undefined): string {
    if (!slug) {
      return '—';
    }
    const nameHe = this.categoryBySlug.get(slug)?.nameHe?.trim();
    return nameHe || slug;
  }

  typeName(slug: string | null | undefined): string {
    if (!slug) {
      return '—';
    }
    const nameHe = this.typeBySlug.get(slug)?.nameHe?.trim();
    return nameHe || slug;
  }

  methodLabel(method: string): string {
    switch (method) {
      case 'embedding_centroid_category':
        return 'קבוצה קרובה';
      case 'embedding_centroid_type':
        return 'תת-קבוצה קרובה';
      case 'verified_neighbor':
        return 'שכן קרוב';
      default:
        return method;
    }
  }

  methodSubtitle(method: string): string | null {
    switch (method) {
      case 'label_phrase_type':
        return 'Embeds the product name and each type phrase (description_he), then ranks types by cosine similarity.';
      case 'label_phrase_category':
        return 'Embeds the product name and each category phrase (description_he), then ranks categories by cosine similarity.';
      case 'embedding_centroid_category':
        return 'Ranks groups by the closest product already in each group (Rami feed or admin).';
      case 'embedding_centroid_type':
        return 'Ranks sub-groups by the closest product already in each sub-group (Rami feed or admin).';
      case 'zero_shot_category':
        return 'Natural-language inference: the product name is the premise; each category description_he fills a Hebrew hypothesis; ranks by entailment probability.';
      case 'zero_shot_type':
        return 'Natural-language inference: the product name is the premise; each type description_he fills a Hebrew hypothesis; ranks by entailment probability.';
      case 'verified_neighbor':
        return 'Finds the closest product already on the Rami tree (Rami feed or admin) by embedding cosine similarity, and copies that product\'s group and sub-group.';
      default:
        return null;
    }
  }

  productImageUrl(productId: number): string {
    return this.api.productImageUrl(productId);
  }

  displayNameSourceLabel(source: string | null | undefined): string {
    switch (source) {
      case 'manual':
        return 'ידני';
      case 'shufersal':
        return 'שופרסל';
      case 'rami_levy':
        return 'רמי לוי';
      case 'rules':
        return 'כללים';
      case 'canonical':
        return 'שם מנורמל';
      case 'name_he':
        return 'שם עברי';
      case 'name_raw':
        return 'שם גולמי';
      default:
        return 'לא נקבע';
    }
  }

  imageCredit(source: string | null | undefined): string {
    if (source === 'rami_levy') {
      return 'תמונה: רמי לוי (שמורה מקומית)';
    }
    if (source === 'shufersal') {
      return 'תמונה: שופרסל (שמורה מקומית)';
    }
    return 'תמונה שמורה מקומית';
  }

  openImagePopup(): void {
    this.cancelCloseImagePopup();
    this.imagePopupOpen = true;
  }

  scheduleCloseImagePopup(): void {
    this.cancelCloseImagePopup();
    this.imagePopupCloseTimer = setTimeout(() => {
      this.imagePopupOpen = false;
      this.imagePopupCloseTimer = null;
    }, 180);
  }

  cancelCloseImagePopup(): void {
    if (this.imagePopupCloseTimer !== null) {
      clearTimeout(this.imagePopupCloseTimer);
      this.imagePopupCloseTimer = null;
    }
  }

  closeImagePopup(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.cancelCloseImagePopup();
    this.imagePopupOpen = false;
  }

  private parseObservationDetail(
    observation: ClassificationObservation
  ): ClassificationObservationDetail | null {
    const raw = observation.detail;
    if (!raw) {
      return null;
    }
    if (typeof raw === 'object') {
      return raw;
    }
    try {
      return JSON.parse(raw) as ClassificationObservationDetail;
    } catch {
      return null;
    }
  }

  private slugLabel(slug: string): string {
    const categoryName = this.categoryBySlug.get(slug)?.nameHe?.trim();
    if (categoryName) {
      return categoryName;
    }
    const typeName = this.typeBySlug.get(slug)?.nameHe?.trim();
    if (typeName) {
      return typeName;
    }
    return slug;
  }

  async switchView(view: 'needs' | 'autosaved'): Promise<void> {
    this.view = view;
    this.errorMessage = '';
    if (view === 'needs') {
      await this.loadProposals();
    } else {
      await this.loadAutosaved();
    }
  }

  async loadProposals(): Promise<void> {
    try {
      const result = await firstValueFrom(this.api.listClassificationProposals());
      this.proposals = result.items;
    } catch {
      this.proposals = [];
    }
  }

  isProposalOpen(item: ClassificationProposal): boolean {
    return this.selectedProposalId === item.id;
  }

  async openProposal(item: ClassificationProposal): Promise<void> {
    if (this.selectedProposalId === item.id) {
      this.selectedProposalId = null;
      this.proposalProducts = [];
      this.proposalProductsLoading = false;
      return;
    }
    this.selectedProposalId = item.id;
    this.proposalProducts = [];
    this.proposalProductsLoading = true;
    try {
      const result = await firstValueFrom(this.api.listClassificationProposalProducts(item.id));
      if (this.selectedProposalId !== item.id) {
        return;
      }
      this.proposalProducts = result.items;
    } catch {
      if (this.selectedProposalId === item.id) {
        this.proposalProducts = [];
      }
    } finally {
      if (this.selectedProposalId === item.id) {
        this.proposalProductsLoading = false;
      }
    }
  }

  async acceptProposal(item: ClassificationProposal, event: Event): Promise<void> {
    event.stopPropagation();
    this.errorMessage = '';
    try {
      await firstValueFrom(this.api.acceptClassificationProposal(item.id));
      this.statusMessage =
        item.kind === 'verify'
          ? `אומת: ${item.nameHe}`
          : item.kind === 'type'
            ? `נוסף וסווג: ${item.nameHe}`
            : `נוסף: ${item.nameHe}`;
      await this.loadCodes();
      await this.loadProposals();
      this.proposalProducts = [];
      this.proposalProductsLoading = false;
      this.selectedProposalId = null;
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.error?.error) {
        this.errorMessage = `אישור ההצעה נכשל: ${error.error.error}`;
      } else {
        this.errorMessage = 'אישור ההצעה נכשל.';
      }
    }
  }

  async rejectProposal(item: ClassificationProposal, event: Event): Promise<void> {
    event.stopPropagation();
    try {
      await firstValueFrom(this.api.rejectClassificationProposal(item.id));
      this.statusMessage = `נדחה למוצרים הנוכחיים: ${item.nameHe}`;
      await this.loadProposals();
      this.proposalProducts = [];
      this.proposalProductsLoading = false;
      this.selectedProposalId = null;
    } catch {
      this.errorMessage = 'דחיית ההצעה נכשלה.';
    }
  }

  async rejectProposalProduct(
    item: ClassificationProposalProduct,
    event: Event
  ): Promise<void> {
    event.stopPropagation();
    if (this.selectedProposalId == null) {
      return;
    }
    try {
      await firstValueFrom(
        this.api.rejectClassificationProposal(this.selectedProposalId, item.productId)
      );
      this.statusMessage = `נדחה למוצר: ${item.displayName}`;
      await this.loadProposals();
      const stillListed = this.proposals.find((p) => p.id === this.selectedProposalId);
      if (stillListed) {
        const result = await firstValueFrom(
          this.api.listClassificationProposalProducts(this.selectedProposalId)
        );
        this.proposalProducts = result.items;
      } else {
        this.proposalProducts = [];
        this.proposalProductsLoading = false;
        this.selectedProposalId = null;
      }
    } catch {
      this.errorMessage = 'דחיית ההצעה למוצר נכשלה.';
    }
  }

  async loadAutosaved(): Promise<void> {
    try {
      const result = await firstValueFrom(
        this.api.listAutosavedClassifications(this.autosavedModel.trim() || undefined)
      );
      this.autosaved = result.items;
    } catch {
      this.autosaved = [];
    }
  }

  async openListedProduct(barcode: string | null): Promise<void> {
    if (!barcode) {
      return;
    }
    this.barcode = barcode;
    await this.previewLookup();
  }

  async runGraph(): Promise<void> {
    if (!this.preview) {
      return;
    }
    this.runningGraph = true;
    this.errorMessage = '';
    this.statusMessage = '';
    try {
      await firstValueFrom(
        this.api
          .runClassificationGraph({
            productId: this.preview.productId
          })
          .pipe(timeout(180_000))
      );
      await this.previewLookup();
      this.statusMessage = 'סיווג המודלים הסתיים.';
      if (this.preview?.graphRun?.outcome === 'queued') {
        this.statusMessage = 'סיווג המודל בתור לאישור ב״צריך אותי״.';
      } else if (this.preview?.graphRun?.outcome === 'auto_saved') {
        this.statusMessage = 'שלושת המודלים הסכימו — נשמר אוטומטית.';
      } else if (this.preview?.graphRun?.outcome === 'disagreement') {
        this.statusMessage = 'המודלים לא הסכימו — בחרו המלצה ואז אמתו.';
      } else if (this.preview?.graphRun?.outcome === 'error') {
        this.statusMessage = '';
        this.errorMessage = 'סיווג המודלים נכשל.';
      }
      if (this.view === 'needs') {
        await this.loadProposals();
      } else {
        await this.loadAutosaved();
      }
    } catch {
      this.errorMessage = 'הרצת סיווג המודלים נכשלה. בדקו את מפתחות ה-API של המודלים.';
    }
    this.runningGraph = false;
  }

  applyModelVote(vote: ClassificationModelVote): void {
    if (!vote.categorySlug || !vote.typeSlug) {
      return;
    }
    this.selectedCategorySlug = vote.categorySlug;
    this.selectedTypeSlug = vote.typeSlug;
    this.statusMessage = `נבחרה המלצת ${this.modelLabel(vote.model)} — לחצו אמת לשמירה.`;
  }

  modelLabel(modelId: string): string {
    return this.classifyModels.find((m) => m.id === modelId)?.label || modelId;
  }

  classificationSourceLabel(source: string | null | undefined, model: string | null | undefined): string {
    if (source === 'llm') {
      return `אוטומטי · ${model || 'llm'}`;
    }
    if (source === 'admin') {
      return 'אומת ידנית';
    }
    return 'עדיין לא מאומת';
  }

  proposalKindLabel(kind: string): string {
    switch (kind) {
      case 'category':
        return 'קבוצה חדשה';
      case 'type':
        return 'תת-קבוצה חדשה';
      case 'attribute_key':
        return 'מאפיין חדש לתת-קבוצה';
      case 'attribute_value':
        return 'ערך מאפיין חדש';
      case 'verify':
        return 'אימות סיווג';
      default:
        return kind;
    }
  }

  outcomeLabel(outcome: string): string {
    switch (outcome) {
      case 'auto_saved':
        return 'נשמר אוטומטית';
      case 'queued':
        return 'בתור לאישור';
      case 'disagreement':
        return 'אין רוב — לא נשמר';
      case 'unlabeled':
        return 'לא נשמר';
      case 'error':
        return 'שגיאה';
      default:
        return outcome;
    }
  }

  stepDecisionLabel(step: ClassificationGraphStep): string {
    switch (step.decision) {
      case 'existing':
        return 'קיים';
      case 'none':
        if (step.name === 'shelf_match') {
          return 'אין';
        }
        return step.name === 'attributes' ? 'ללא מאפיינים' : 'חסר בקטלוג';
      case 'clue':
        return 'רמז';
      case 'abstain':
        return 'נמנע';
      case 'pass':
        return 'עבר';
      case 'fail':
        return 'נכשל';
      case 'auto_saved':
        return 'נשמר אוטומטית';
      case 'disagreement':
        return 'אין הסכמה';
      case 'queued':
        return 'בתור';
      case 'unlabeled':
        return 'לא נשמר';
      case 'error':
        return 'שגיאה';
      default:
        return step.decision;
    }
  }

  stepTitle(name: string): string {
    switch (name) {
      case 'group':
      case 'category':
      case 'aisle': // legacy step name from older graph runs
        return 'קבוצה';
      case 'sub_group':
      case 'type':
        return 'תת-קבוצה';
      case 'shelf_match':
        return 'התאמת שם';
      case 'attributes':
        return 'מאפיינים';
      case 'cheap_check':
        return 'בדיקה זולה';
      case 'llm_consensus':
        return 'הסכמת מודלים';
      case 'save':
        return 'שמירה';
      default:
        return name;
    }
  }

  formatUsd(value: number | null | undefined): string {
    if (value == null) {
      return '$0';
    }
    return `$${Number(value).toFixed(6)}`;
  }

  private pickSuggestedCategory(preview: ClassificationPreview): string | null {
    const categoryOnly = preview.observations.filter((o) => o.categorySlug && !o.typeSlug);
    const rows = categoryOnly.length > 0
      ? categoryOnly
      : preview.observations.filter((o) => o.categorySlug);
    return this.highestConfidence(rows)?.categorySlug ?? null;
  }

  private pickSuggestedType(preview: ClassificationPreview): string | null {
    const rows = preview.observations.filter((o) => o.typeSlug);
    return this.highestConfidence(rows)?.typeSlug ?? null;
  }

  private highestConfidence(
    rows: ClassificationPreview['observations']
  ): ClassificationPreview['observations'][number] | undefined {
    if (rows.length === 0) {
      return undefined;
    }
    return rows.reduce((best, row) => (row.confidence > best.confidence ? row : best));
  }

  private applyVerifiedToPreview(
    categorySlug: string | null,
    typeSlug: string | null,
    verifiedAt: string | null,
    verifiedBy: string | null
  ): void {
    if (!this.preview) {
      return;
    }
    this.preview = {
      ...this.preview,
      verifiedCategorySlug: categorySlug,
      verifiedTypeSlug: typeSlug,
      verifiedAt,
      verifiedBy
    };
  }
}
